<template>
  <v-card class="report-builder-card" elevation="2" rounded="lg">
    <v-toolbar flat>
      <v-toolbar-title class="text-h6">
        <v-icon class="mr-2" color="primary">mdi-file-document-multiple</v-icon>
        Berichte
      </v-toolbar-title>
      <v-spacer />
      <ProjectPageNav class="mr-2" />
      <!-- `HelpBar` ist nur die Schublade; geoeffnet wird sie ueber
           `store.showSidebar(<Hilfekomponente>)` — so wie auf der Seite
           Einsparpotential. -->
      <v-tooltip text="Hilfe anzeigen" location="bottom" :disabled="store.sidebarShown">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            color="primary"
            variant="flat"
            class="mr-2"
            @click="store.sidebarShown ? store.hideSidebar() : store.showSidebar(ReportsHelp)"
          >
            <v-icon start>
              {{ store.sidebarShown ? 'mdi-close' : 'mdi-help-circle-outline' }}
            </v-icon>
            Hilfe
          </v-btn>
        </template>
      </v-tooltip>
    </v-toolbar>

    <v-card-text>
      <div class="text-subtitle-2 mb-1">Berichtstyp</div>
      <v-radio-group v-model="reportType" density="comfortable" hide-details class="mb-3">
        <v-radio label="Einsparpotentialbericht (Word)" value="einsparpotentialbericht" />
        <v-radio label="Energieaudit DIN EN 16247-1 (Word)" value="din16247" />
        <v-radio label="Maßnahmen-Kurzberichte (ZIP)" value="zip" />
        <v-radio label="Bewertungsberichte DIN EN 17463 / VALERI (ZIP)" value="valeri" />
      </v-radio-group>

      <!-- „Einstellung für Sensitivitätsanalyse" der VALERI-Vorlage: um wie viel
           Prozent jede Eingangsgrösse in Anhang 5.4 ausgelenkt wird. -->
      <v-text-field
        v-if="reportType === 'valeri'"
        v-model="sensitivityPercent"
        label="Auslenkung der Sensitivitätsanalyse"
        suffix="%"
        type="number"
        min="0"
        max="100"
        density="compact"
        hide-details="auto"
        class="mb-2 sensitivity-field"
        :error-messages="sensitivityError"
      />

      <!--
        Der Hinweis stand bis jetzt **im Kurzbericht selbst**, in roter Schrift,
        zusammen mit dem Satz „gilt vor Abgabe zu entfernen". Er ging damit an
        den Kunden statt an den Auditor, und wer ihn zu entfernen vergass,
        lieferte die Bedienungsanleitung mit aus.
        Er gehoert dorthin, wo die Entscheidung faellt: an die Auswahl des
        Berichtstyps. Der zweite Satz der Vorlage ist ersatzlos entfallen — er
        warnte vor dem roten Text, den es nicht mehr gibt.
      -->
      <v-alert
        v-if="reportType === 'zip'"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        Die Kurzberichte sind als <b>Vorlage</b> gedacht: Sie visualisieren die Eckdaten
        je Maßnahme. Tabellen dürfen nach dem Download angepasst und um eigenen Text
        ergänzt werden.
      </v-alert>

      <v-switch
        v-if="reportType === 'einsparpotentialbericht'"
        v-model="includeAppendix"
        label="Analyse-Anhang mit Diagrammen"
        color="primary"
        density="compact"
        hide-details
        class="mb-2"
      />

      <div v-if="showModuleSelection">
        <div class="text-subtitle-2 mb-1">Anhänge hinzufügen</div>
        <v-checkbox
          v-for="module in moduleOptions"
          :key="module.value"
          v-model="selectedModules"
          :value="module.value"
          :label="module.label"
          density="compact"
          hide-details
        />
        <div class="text-caption text-medium-emphasis mt-1">
          Nicht geöffnete Seiten werden automatisch im Hintergrund erfasst.
        </div>
      </div>

      <v-btn
        color="primary"
        class="mt-4"
        :loading="isGenerating"
        :disabled="isGenerating || !canGenerate"
        @click="generate"
      >
        <v-icon start>mdi-download</v-icon>
        Bericht erstellen
      </v-btn>
    </v-card-text>

    <!-- Offscreen renderer: auto-captures the page charts without the user
         having to visit each page first. -->
    <div v-if="showOffscreen" class="offscreen-docx-renderer" aria-hidden="true">
      <ProjectEnergyFlow ref="energyRendererRef" />
      <ProjectBenchmarking ref="benchmarkRendererRef" />
      <ProjectCalcValeri ref="valeriRendererRef" />
    </div>
  </v-card>
</template>

<script setup>
import { ref, computed } from "vue";
import { toast } from "vue3-toastify";
import api from "@/plugins/axios";
import { getDocxCombinedCharts } from "@/utils/docxAppendixCache";
import ProjectPageNav from "@/components/ProjectPageNav.vue";
import ReportsHelp from "@/components/help/ReportsHelp.vue";
import { useProjectStore } from "@/stores/projectStore";

const store = useProjectStore();
import ProjectEnergyFlow from "@/views/ProjectEnergyFlow.vue";
import ProjectBenchmarking from "@/views/ProjectBenchmarking.vue";
import ProjectCalcValeri from "@/views/ProjectCalcValeri.vue";

const props = defineProps({
  projectId: { type: [String, Number], default: null },
});

const reportType = ref("einsparpotentialbericht");
const includeAppendix = ref(true);
const selectedModules = ref(["Energiebilanz", "Benchmarking", "VALERI"]);
const isGenerating = ref(false);
// Vorgabe der VALERI-Vorlage für Anhang 5.4.
const sensitivityPercent = ref(15);

const showOffscreen = ref(false);
const energyRendererRef = ref(null);
const benchmarkRendererRef = ref(null);
const valeriRendererRef = ref(null);

const moduleOptions = [
  { value: "Energiebilanz", label: "Energiebilanz (Sankey)" },
  { value: "Benchmarking", label: "Benchmarking" },
  { value: "VALERI", label: "VALERI (Wirtschaftlichkeit)" },
];

const showModuleSelection = computed(
  () => reportType.value === "din16247" || (reportType.value === "einsparpotentialbericht" && includeAppendix.value),
);

/*
 * Die Auslenkung geht als Zahl an den Server, der sie gegen 0…100 % prüft und
 * sonst mit 400 antwortet. Hier abzufangen erspart dem Nutzer den Umweg über
 * eine Fehlermeldung, die er erst nach dem Klick sieht.
 */
const sensitivityError = computed(() => {
  if (reportType.value !== "valeri") return "";
  const wert = Number(sensitivityPercent.value);
  if (!Number.isFinite(wert)) return "Bitte eine Zahl angeben.";
  if (wert < 0 || wert > 100) return "Die Auslenkung muss zwischen 0 und 100 % liegen.";
  return "";
});

const canGenerate = computed(() => Boolean(props.projectId) && !sensitivityError.value);

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const waitFor = async (predicate, timeoutMs = 25000, intervalMs = 250) => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (predicate()) {
      return true;
    }
    await sleep(intervalMs);
  }
  return false;
};

async function collectOffscreenCharts() {
  showOffscreen.value = true;
  await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));

  const ready = await waitFor(() =>
    Boolean(
      energyRendererRef.value?.calcResponse &&
        benchmarkRendererRef.value?.calcResponse &&
        valeriRendererRef.value?.scenarioResponses?.probable,
    ),
  );
  if (!ready) {
    throw new Error("Offscreen chart rendering timeout.");
  }

  await sleep(600);

  const energyCharts = await energyRendererRef.value.collectEnergyChartsForDocx();
  const benchmarkCharts = await benchmarkRendererRef.value.collectBenchmarkChartsForDocx();
  const valeriCharts = await valeriRendererRef.value.collectValeriChartsForDocx();

  showOffscreen.value = false;
  return [...energyCharts, ...benchmarkCharts, ...valeriCharts];
}

// Collect charts for the selected modules, preferring live offscreen capture and
// falling back to the persisted cache.
async function collectCharts(modules) {
  const wanted = new Set(modules);
  let charts = [];
  try {
    charts = await collectOffscreenCharts();
  } catch {
    showOffscreen.value = false;
    charts = getDocxCombinedCharts(props.projectId);
    toast.warning("Diagramme konnten nicht automatisch erzeugt werden – zwischengespeicherte Diagramme werden genutzt.");
  }
  return charts.filter((chart) => wanted.has(chart?.module));
}

function downloadBlob(response, fallbackName) {
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  link.download = match?.[1]?.replace(/['"]/g, "") || fallbackName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function generate() {
  if (!props.projectId) {
    toast.error("Projekt-ID fehlt.");
    return;
  }
  isGenerating.value = true;
  try {
    const id = props.projectId;

    if (reportType.value === "zip") {
      const response = await api.get(`/projects/${id}/action_reports_zip/`, { responseType: "blob" });
      downloadBlob(response, `Massnahmenberichte_${id}.zip`);
      return;
    }

    if (reportType.value === "valeri") {
      const response = await api.get(`/projects/${id}/valeri_reports_zip/`, {
        params: { sensitivity: Number(sensitivityPercent.value) },
        responseType: "blob",
      });
      downloadBlob(response, `Bewertungsberichte_VALERI_${id}.zip`);
      return;
    }

    if (reportType.value === "einsparpotentialbericht" && !includeAppendix.value) {
      const response = await api.get(`/projects/${id}/docx/`, { responseType: "blob" });
      downloadBlob(response, `Einsparpotentialbericht_${id}.docx`);
      return;
    }

    const charts = await collectCharts(selectedModules.value);
    const meta = { source: "report-builder", schemaVersion: "1.0", exportedAt: new Date().toISOString() };

    if (reportType.value === "din16247") {
      const response = await api.post(`/projects/${id}/din16247_docx/`, { charts, meta }, { responseType: "blob" });
      downloadBlob(response, `Energieaudit_DIN_EN_16247-1_${id}.docx`);
    } else {
      const response = await api.post(`/projects/${id}/docx_extended/`, { charts, meta }, { responseType: "blob" });
      downloadBlob(response, `Einsparpotentialbericht_inkl_Anhang_${id}.docx`);
    }
  } catch {
    toast.error("Bericht konnte nicht erstellt werden.");
  } finally {
    isGenerating.value = false;
    showOffscreen.value = false;
  }
}

</script>

<style scoped>
.report-builder-card {
  margin-top: 1rem;
}

.offscreen-docx-renderer {
  position: fixed;
  left: -10000px;
  top: 0;
  width: 1300px;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
}
</style>
